import {
  Button,
  Card,
  Checkbox,
  Field,
  Input,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Plus } from "lucide-react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Camelize } from "camelize-ts";
import { useEffect, useState } from "react";
import { CustomerBodySchema, CustomerSchema } from "../../generated";
import { useCustomerCreate } from "../../hooks/useCustomers";
import { useBrreg } from "../../hooks/useBrreg";
import { useBrregSearch } from "../../hooks/useBrregSearch";
import { useCustomerUpdate } from "../../hooks/useCustomerUpdate";
import { useDebounceState } from "../../hooks/useDebounceState";

const schema = yup
  .object({
    customerNumber: yup.number().positive().max(9999999), // Max 7 digits
    name: yup.string().required(),
    email: yup.string().email().ensure(),
    orgNumber: yup
      .string()
      .matches(/^(.{9}|)$/, { excludeEmptyString: true })
      .ensure(),
    phoneNumber: yup
      .string()
      .matches(/^(.{8}|)$/, { excludeEmptyString: true })
      .ensure(),
  })
  .required();

export const CustomerForm = ({
  customer,
  setCustomer,
}: {
  customer: Camelize<CustomerSchema> | null;
  setCustomer: (customerNumber: Camelize<CustomerSchema>) => void;
}) => {
  const customerCreateMutation = useCustomerCreate({
    onSuccessCallback: setCustomer,
  });
  const customerUpdateMutation = useCustomerUpdate(
    customer?.customerNumber || null,
  );

  const [enterCustomerNumber, setEnterCustomerNumber] = useState(false);
  const [brregQuery, setBrregQuery] = useState("");
  const debouncedBrregQuery = useDebounceState(brregQuery);
  const brregSearch = useBrregSearch(debouncedBrregQuery);

  const {
    register,
    unregister,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors: formStateErrors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
    values: {
      customerNumber: customer?.customerNumber,
      name: customer?.name || "",
      email: customer?.email || "",
      orgNumber: customer?.orgNumber || "",
      phoneNumber: customer?.phoneNumber || "",
    },
  });
  const orgNumber = getValues("orgNumber") || "";
  const brreg = useBrreg(orgNumber);

  useEffect(() => {
    const values = getValues();
    reset({
      ...values,
      customerNumber: values?.customerNumber,
      name: values?.name || brreg.data?.navn,
    });
  }, [reset, getValues, brreg.data]);

  useEffect(() => {
    reset({});
  }, [reset, customerCreateMutation.isSuccess]);

  const onSubmit = (data: Camelize<CustomerBodySchema>) => {
    if (customer?.customerNumber) {
      return customerUpdateMutation.mutate(data);
    }
    return customerCreateMutation.mutate(data);
  };
  const isLoading =
    customerCreateMutation.isPending || customerUpdateMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack>
        <Stack gap={1}>
          <Input
            placeholder="Søk i Brønnøysundregistrene..."
            value={brregQuery}
            onChange={(e) => setBrregQuery(e.target.value)}
          />
          {brregSearch.isLoading && <Spinner size="sm" />}
          {brregSearch.data && brregSearch.data.length > 0 && (
            <Stack gap={1}>
              {brregSearch.data.map((enhet) => (
                <Card.Root
                  key={enhet.organisasjonsnummer}
                  size="sm"
                  p={2}
                  cursor="pointer"
                  _hover={{ bg: "bg.muted" }}
                  onClick={() => {
                    setValue("orgNumber", enhet.organisasjonsnummer);
                    setValue("name", enhet.navn);
                    setBrregQuery("");
                  }}
                >
                  <Text fontSize="sm" fontWeight="medium">
                    {enhet.navn}
                  </Text>
                  <Text fontSize="xs" color="fg.muted">
                    Org.nr: {enhet.organisasjonsnummer}
                  </Text>
                </Card.Root>
              ))}
            </Stack>
          )}
        </Stack>
        <Field.Root>
          <Stack direction="row">
            <Field.Label>Manuelt kundenummer</Field.Label>
            <Checkbox.Root
              size="lg"
              onCheckedChange={(details) => {
                if (!details.checked) {
                  unregister("customerNumber");
                }
                setEnterCustomerNumber(Boolean(details.checked));
              }}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
            </Checkbox.Root>
          </Stack>
        </Field.Root>
        {enterCustomerNumber && (
          <Field.Root
            invalid={Boolean(formStateErrors["customerNumber"])}
            minWidth={300}
          >
            <Input
              {...register("customerNumber")}
              placeholder="Kundenummer autogenereres om man ikke legger det inn"
            />
          </Field.Root>
        )}
        <Field.Root
          invalid={Boolean(formStateErrors["orgNumber"])}
          minWidth={300}
        >
          <Field.Label>Organisasjonsnummer</Field.Label>
          <Input {...register("orgNumber")} placeholder="987654321" />
        </Field.Root>
        <Field.Root
          invalid={Boolean(formStateErrors["name"])}
          minWidth={300}
        >
          <Field.Label>Kundenavn</Field.Label>
          <Input {...register("name")} placeholder="Ola Nordmann" />
        </Field.Root>
        <Field.Root
          invalid={Boolean(formStateErrors["email"])}
          minWidth={300}
        >
          <Field.Label>E-post</Field.Label>
          <Input {...register("email")} placeholder="mail@example.com" />
        </Field.Root>
        <Field.Root
          invalid={Boolean(formStateErrors["phoneNumber"])}
          minWidth={300}
        >
          <Field.Label>Mobiltelefon</Field.Label>
          <Input {...register("phoneNumber")} placeholder="999 99 999" />
        </Field.Root>
        <Button
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? <Spinner /> : <Plus />} Lagre kunde
        </Button>
      </Stack>
    </form>
  );
};
